import { useEffect, useState } from "react";
import { Clock, LogOut, MessageCircle, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ChatPanel } from "../components/ChatPanel";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const CHAT_MS = 10 * 60 * 1000;

export default function PendingActivation() {
  const { user, refreshUser, logout } = useAuth();
  const [form, setForm] = useState({ old_username: "", whatsapp: "" });
  const [busy, setBusy] = useState(false);
  const [nowTs, setNowTs] = useState(Date.now());
  const [hasMessages, setHasMessages] = useState(false);
  const submitted = !!(user?.old_username && user?.whatsapp);
  const chatEnd = submitted && user?.onboarding_at ? new Date(user.onboarding_at).getTime() + CHAT_MS : 0;
  const chatOpen = chatEnd > nowTs;

  useEffect(() => {
    const t = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const remain = Math.max(0, chatEnd - nowTs);
  const mm = String(Math.floor(remain / 60000)).padStart(2, "0");
  const ss = String(Math.floor((remain % 60000) / 1000)).padStart(2, "0");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/onboarding", form);
      toast.success("Data terkirim — live chat dengan admin dibuka 10 menit");
      refreshUser();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  // chat otomatis terbuka jika admin mengirim pesan lebih dulu (bimbingan pendaftaran)
  useEffect(() => {
    if (!user?.id) return undefined;
    const check = () => api.get(`/chat/agent:${user.id}/messages`).then((r) => setHasMessages((r.data || []).length > 0)).catch(() => {});
    check();
    const t = setInterval(check, 15000);
    return () => clearInterval(t);
  }, [user?.id]);

  const chatSection = (
    <div className="text-left space-y-2" data-testid="pending-chat-section">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-semibold text-slate-200 flex items-center gap-1.5">
          <MessageCircle className="w-3.5 h-3.5 text-[#F04E23]" /> Live Chat Admin
        </p>
        {chatOpen && <span className="text-[10px] font-mono text-amber-400" data-testid="pending-chat-countdown">Sisa {mm}:{ss}</span>}
      </div>
      <p className="text-[10px] text-slate-500">Chat dengan admin — untuk komunikasi pendaftaran/aktivasi akun.</p>
      <ChatPanel channel={`agent:${user.id}`} testid="pending-chat" inputClass="text-white placeholder:text-slate-500" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#050914] flex items-center justify-center p-6 relative overflow-hidden" data-testid="pending-page">
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_50%_20%,rgba(240,78,35,0.12),transparent_60%)]" aria-hidden="true" />
      <div className="w-full max-w-sm bg-[#0A1020] border border-[#1A2644] rounded-3xl p-8 shadow-2xl shadow-black/60 text-center space-y-4 relative z-10">
        <div className="inline-block rounded-2xl px-6 py-3 bg-[#040810] border border-white/10 shadow-inner">
          <img src="/logo.png?v=3" alt="Fasnet Fiber" className="h-12 w-auto object-contain mx-auto" />
        </div>
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-400 text-xs font-semibold" data-testid="pending-badge">
          <Clock className="w-3.5 h-3.5" /> Menunggu Aktivasi Admin
        </div>
        <p className="text-sm text-slate-400">Akun Google <b className="text-slate-200">{user?.email}</b> belum tertaut ke akun agen Fasnet.</p>
        {!submitted ? (
          <>
          <form onSubmit={submit} className="space-y-3 text-left">
            <p className="text-xs text-slate-400">Isi data berikut agar admin dapat menautkan email Anda ke akun agen yang sudah ada di sistem. <b className="text-slate-300">Admin juga bisa mengirim pesan lebih dulu</b> — live chat akan muncul otomatis di halaman ini.</p>
            <div className="space-y-1.5">
              <Label className="text-xs">Username Akun Lama (sistem lama)</Label>
              <Input required value={form.old_username} onChange={(e) => setForm({ ...form, old_username: e.target.value })} placeholder="cth: johndoe01" className="text-white placeholder:text-slate-500" data-testid="input-old-username" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Nomor WhatsApp Aktif</Label>
              <Input required value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} placeholder="cth: 0812xxxxxxx" inputMode="tel" className="text-white placeholder:text-slate-500" data-testid="input-whatsapp" />
            </div>
            <Button type="submit" className="w-full bg-[#F04E23] hover:bg-[#D83E15] text-white" disabled={busy} data-testid="btn-submit-onboarding">
              <UserCheck className="w-4 h-4 mr-1.5" /> {busy ? "Mengirim..." : "Kirim ke Admin"}
            </Button>
          </form>
          {hasMessages && chatSection}
          </>
        ) : (
          <>
            <div className="rounded-xl border border-white/10 bg-white/5 p-4 text-xs text-slate-300 space-y-1.5 text-left" data-testid="pending-submitted">
              <p>Data Anda sudah terkirim:</p>
              <p>Akun lama: <b>{user.old_username}</b> • WA: <b>{user.whatsapp}</b></p>
              <p className="text-slate-400 pt-1">Admin akan menautkan email ini ke akun agen Anda. Setelah ditautkan, <b>login ulang dengan Gmail yang sama</b> lalu masukkan PIN akun Anda.</p>
            </div>
            {chatOpen || hasMessages ? (
              chatSection
            ) : (
              <p className="text-[11px] text-slate-500" data-testid="pending-chat-expired">
                Sesi live chat 10 menit sudah berakhir. Data Anda sudah diterima admin — silakan login ulang setelah akun ditautkan.
              </p>
            )}
          </>
        )}
        <Button variant="ghost" className="w-full text-slate-400" onClick={logout} data-testid="btn-pending-logout"><LogOut className="w-4 h-4 mr-1.5" /> Keluar / Ganti Akun</Button>
      </div>
    </div>
  );
}
